import dayjs from 'dayjs'
import COUNTRIES_EN from 'world_countries_lists/data/countries/en/world.json'

import DumbbellSVG from '@/images/home/categories/Dumbbell.svg?react'
import RunningFigureSVG from '@/images/home/categories/RunningFigure.svg?react'
import GraduationCapSVG from '@/images/home/categories/GraduationCap.svg?react'
import { calculRegistrationState } from '@/middlewares/services/home.services'
import { getRegistrationStatusDisplay } from '@/components/common/StatusDot/index.utils'
import { UK_NATIONS } from '@/components/common/countryOptions'
import { formatLocalDate } from '@/utils/DateHelpers'

/** Category → icon component, shared by the hero labels and the settings sidebar. */
export const CATEGORY_ICON = {
    'Functional Fitness': DumbbellSVG,
    'Hybrid Race': RunningFigureSVG,
    Workshop: GraduationCapSVG,
}

/**
 * Root path of the event hub for absolute links (tabs, registration, reviews): keeps the
 * `/sporthub` prefix so deep links and refreshes land on the same shell.
 */
export const getEventHubBase = (pathname, eventIdentifier) =>
    pathname.startsWith('/sporthub/') ? `/sporthub/${eventIdentifier}` : `/${eventIdentifier}`

/** "Jun 10, 2026" / "10 juin 2026" in the event timezone and the user's language (i18n.resolvedLanguage). */
export const formatLongDate = (iso, tz, language) => formatLocalDate(iso, { language, tz })

/**
 * Registration + spectator + staff state as the sidebar card shows it. Shared with the mobile
 * registration bar (mock `paintRegBar` reads the card's own fields) so both agree.
 * calculRegistrationState → 'REGISTER NOW' | 'SOLD OUT' | `OPEN AT <iso>` | false.
 * Spectators (William, 15 Oct 2026): `_spectatorTicketing` is `{ publish, open_date, end_date,
 * total_spots }` from the public presentation, or `null` when the launch gate is off or no shop
 * exists — the backend folds the gate in, so no `spectatorsEnabled` check here. `total_spectators`
 * counts booked tickets and live holds, like the shop's own "Sold out".
 */
export const getSidebarRegState = (competition, tHome, tMonths, language) => {
    const tz = competition?.timezone
    const isOpenAt = (s) => typeof s === 'string' && s.includes('OPEN AT')
    const regStatus = calculRegistrationState(
        competition?._ticketing,
        competition?.divisions,
        competition?.total_participants,
    )
    const volStatus = calculRegistrationState(competition?._volunteering, null, competition?.total_volunteers)
    const specStatus = calculRegistrationState(competition?._spectatorTicketing, null, competition?.total_spectators)
    const regSoon = isOpenAt(regStatus)
    const volSoon = isOpenAt(volStatus)
    const specSoon = isOpenAt(specStatus)
    const regDisplay = getRegistrationStatusDisplay(regStatus, tHome, tMonths, tz)
    const specDisplay = getRegistrationStatusDisplay(specStatus, tHome, tMonths, tz)
    return {
        regOpen: regStatus === 'REGISTER NOW',
        regSoon,
        regSoldOut: regStatus === 'SOLD OUT',
        volOpen: volStatus === 'REGISTER NOW',
        volSoon,
        specOpen: specStatus === 'REGISTER NOW',
        specSoon,
        specSoldOut: specStatus === 'SOLD OUT',
        regColor: regDisplay.color,
        // "Open May 17 at 19:30" while upcoming, "Registration Open" / "Sold Out" otherwise
        regLabel: regDisplay.label,
        volSoonLabel: volSoon ? getRegistrationStatusDisplay(volStatus, tHome, tMonths, tz).label : '',
        specColor: specDisplay.color,
        specLabel: specDisplay.label,
        regStart: formatLongDate(competition?._ticketing?.open_date, tz, language),
        regEnd: formatLongDate(competition?._ticketing?.end_date, tz, language),
        specEnd: formatLongDate(competition?._spectatorTicketing?.end_date, tz, language),
    }
}

/** "Europe/Paris (+02:00)"— mirrors EventDetailsEditor's getDynamicTzText. */
export const getTimezoneText = (tz, iso) => {
    if (!tz) return ''
    const offset = dayjs(iso || undefined)
        .tz(tz)
        .format('Z')
    return `${tz} (${offset})`
}

// Country is stored as a NAME ("France"), like CountryInput emits. Map it to the
// ISO alpha-2 code so react-country-flag can render the flag.
const NAME_TO_ALPHA2 = [...COUNTRIES_EN, ...UK_NATIONS].reduce((acc, c) => {
    acc[c.name.toLowerCase()] = c.alpha2
    return acc
}, {})

export const countryAlpha2 = (countryName) =>
    countryName ? NAME_TO_ALPHA2[String(countryName).toLowerCase()] : undefined

/** Figma division card title — sentence case, not ALL CAPS (e.g. "Double open femme"). */
export const formatEventHubDivisionName = (name) => {
    if (!name) {
        return ''
    }

    if (name !== name.toUpperCase()) {
        return name
    }

    const lower = name.toLowerCase()
    return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/** Partners strip → section title gap: the mock `.ev-main{gap:14px}` step. */
export const EVENT_HUB_PARTNERS_MARGIN_BOTTOM_CLASS = 'mb-3.5'

/** Section title — mock `.ev-panel h2`: SFT display 24px Bold, letter-spacing -.01em, 20px under 600px. */
export const EVENT_HUB_SECTION_TITLE_CLASS =
    'font-display m-0 text-[24px] font-bold leading-[1.45] tracking-[-0.01em] text-[#0e1017] max-[600px]:text-[20px]'

/** Organizer edit/confirm/cancel square control (Figma 3272:6931 / 3272:7356, mock `.org-ico.s27` — 27px, 5px radius). */
export const EVENT_HUB_EDIT_CONTROL_CLASS =
    's-focus-ring flex size-[27px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border p-1.25 text-[13px]'

/** Same control at 23px (mock `.org-ico.s23`): hero title tools, partner / photo trash, upload row ×. */
export const EVENT_HUB_EDIT_CONTROL_SM_CLASS =
    's-focus-ring flex size-[23px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border p-0 text-[11px]'

/** Same control at 30px (mock `.org-ico.s30`): the side panel pencil. */
export const EVENT_HUB_EDIT_CONTROL_MD_CLASS =
    's-focus-ring flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border p-1.25 text-[14px]'

/**
 * Organizer edit affordances are blue on the settings screens (Figma 3790:19780):
 * the dashed outline around the publish/settings bar, the dashed rule between a
 * section title and its pencil, and the pencil buttons themselves.
 */
export const EVENT_HUB_EDIT_ACCENT_CLASS = 'border-[#1672ec] text-[#1672ec]'

/**
 * Dotted rule filling the gap between a section title and its edit control (Figma
 * 3790:20043). The Figma line is 1px; the mock renders it 2px dotted because a 1px
 * dotted line disappears on screen (mock `.org-dots`).
 */
// border-0 first: `border-dotted` styles all four sides and nothing resets the UA `medium`
// width here, so without it the other three sides draw 3px dots too (caps at both ends).
export const EVENT_HUB_EDIT_RULE_CLASS = 'h-0 min-w-px flex-1 border-0 border-t-2 border-dotted border-[#1672ec]'

/** Title · dotted rule · pencil row (mock `.org-h`): 16px gaps, 4px under. */
export const EVENT_HUB_EDIT_HEADER_CLASS = 'flex items-center gap-4'

/*
 * Cancel / confirm pair shown while a section is being edited: an outlined ✗ and
 * a filled ✓ (Figma 3790:25486 / 3790:25488). Pair either set with
 * EVENT_HUB_EDIT_CONTROL_CLASS.
 */

/** Same square control at the 33px size the workout rows use (Figma 3790:20796). */
export const EVENT_HUB_EDIT_CONTROL_LG_CLASS =
    's-focus-ring flex size-8.25 shrink-0 cursor-pointer items-center justify-center rounded-[5px] border p-1.25 text-[13px]'

/**
 * Destructive square control — red outline, e.g. delete a workout (Figma 3790:20801).
 * Hover fills it solid red with a white glyph (Figma 3790:23165), mirroring the way
 * the blue edit control fills.
 */
export const EVENT_HUB_DELETE_CONTROL_CLASS =
    'border-[#b93a2c] bg-white text-[#b93a2c] transition-colors hover:bg-[#b93a2c] hover:text-white'

/**
 * "Program publication" picker — a 200.5x33 #f7f7f9 field (Figma 3790:20793). antd's
 * DatePicker is restyled through descendant selectors on its wrapper, same approach
 * as the settings sidebar's FIELD_SKIN.
 */
export const EVENT_HUB_WORKOUT_DATE_SKIN = [
    '[&_.ant-picker]:h-[33px]',
    '[&_.ant-picker]:w-[200.5px]',
    '[&_.ant-picker]:max-w-full',
    '[&_.ant-picker]:bg-[#f7f7f9]',
    '[&_.ant-picker]:border-[#dbdadf]',
    // Hover and focus both darken the border to #272727 (Figma 3790:23157 / 3790:23605)
    // rather than antd's blue focus treatment. These outrank antd's own rules — two
    // classes plus the state class beat its single `.ant-picker:hover`.
    '[&_.ant-picker:hover]:border-[#272727]',
    '[&_.ant-picker-focused]:border-[#272727]',
    '[&_.ant-picker-focused]:shadow-none',
    '[&_.ant-picker]:rounded-[5px]',
    '[&_.ant-picker-input>input]:text-[#272727]',
].join(' ')

/**
 * Date and time read as two fields split by a rule (Figma 3790:23606). dayjs passes
 * `[|]` through literally, so the value still parses back when typed — which a
 * function-valued `format` would have cost us.
 */
export const EVENT_HUB_WORKOUT_DATE_FORMAT = 'DD/MM/YYYY [|] HH:mm'

/**
 * The ✗ and ✓ sit 5px apart wherever the pair appears — division card, description
 * header, settings sidebar, hero title (Figma 3790:24504 / 3790:25485 / 3790:25507).
 */
export const EVENT_HUB_CONTROL_PAIR_CLASS = 'flex items-center gap-1.25'

/** On the white page surfaces — description header, settings sidebar. */
export const EVENT_HUB_CANCEL_CONTROL_CLASS =
    'border-[#1672ec] bg-transparent text-[#1672ec] transition-colors hover:bg-[#1672ec]/10'
export const EVENT_HUB_CONFIRM_CONTROL_CLASS =
    'border-[#1672ec] bg-[#1672ec] text-white transition-colors hover:bg-[#1672ec]/85'

/**
 * Over the dimmed hero banner, where blue would not read against the photo, the
 * pencil and the ✗ go translucent white and the ✓ solid white (Figma 3790:25239 /
 * 3790:25237, mock `.bn-title-tools .org-ico`).
 */
export const EVENT_HUB_HERO_EDIT_CONTROL_CLASS =
    'border-white/60 bg-white/12 text-white transition-colors hover:bg-white/30'
export const EVENT_HUB_HERO_CANCEL_CONTROL_CLASS = EVENT_HUB_HERO_EDIT_CONTROL_CLASS
export const EVENT_HUB_HERO_CONFIRM_CONTROL_CLASS =
    'border-white bg-white text-[#272727] transition-colors hover:bg-white/85'

/*
 * Hover states of the two editable image regions in the hero: the region dims to
 * #272727/70 behind a 5px blur and reveals its "Edit" chip (Figma 3790:24788 for
 * the banner, 3790:24793 for the logo tile).
 *
 * Desktop only — a touch layout never gets :hover, so there the region stays
 * undimmed with its chip permanently visible. While a chip is faded out it also
 * stops taking clicks, so an invisible chip is never clickable; keyboard focus
 * still reaches it, hence the focus-within pairs.
 *
 * The banner contains the logo, so the two regions need *named* groups to stay
 * independent — and Tailwind only sees group names written out in full, which is
 * why these are spelled per region instead of taking the name as an argument.
 */
/*
 * Chip sitting on the dimmed banner — "Edit" and "Reposition" share the skin
 * (Figma 3790:24908 / 3790:25886, mock `.bn-btn`: 32px tall, 15px sides, white at
 * 90%, 12px ink text, solid white on hover). Reposition stays solid white while
 * its mode is on (Figma 3790:26092).
 *
 * Once the image exists the labeled chip would sit over the photo / cover most of
 * the logo tile (always visible on touch), so it folds into the icon-only square:
 * same 32px band, glyph only, label kept as aria-label + tooltip (William 14 Oct 2026).
 */
export const EVENT_HUB_BANNER_CHIP_CLASS =
    's-focus-ring flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[5px] border-0 px-[15px] text-xs transition-colors'
export const EVENT_HUB_BANNER_ICON_CHIP_CLASS =
    's-focus-ring flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[5px] border-0 p-0 text-[15px] transition-colors'
export const EVENT_HUB_BANNER_CHIP_IDLE_CLASS = 'bg-white/90 text-[#272727] hover:bg-white'
export const EVENT_HUB_BANNER_CHIP_ACTIVE_CLASS = 'bg-white text-[#272727]'

/** Status chips beside the banner tools: same 32px band, 10px sides (mock `.bn-hint` / `.bn-err`). */
export const EVENT_HUB_BANNER_NOTE_CLASS =
    'flex h-8 shrink-0 items-center gap-[5px] rounded-[5px] px-2.5 text-xs whitespace-nowrap max-nav:h-auto max-nav:max-w-[240px] max-nav:whitespace-normal max-nav:py-1.5'
export const EVENT_HUB_BANNER_HINT_CLASS = `${EVENT_HUB_BANNER_NOTE_CLASS} bg-white/90 text-[#272727]`
export const EVENT_HUB_BANNER_ERROR_CLASS = `${EVENT_HUB_BANNER_NOTE_CLASS} border border-[#b93a2c] bg-[#f8ebe9] text-[#b93a2c]`

/** Default vertical focal point of the banner, as a percentage for object-position. */
export const EVENT_HUB_BANNER_POSITION_DEFAULT = 50

/**
 * Held open, not hover-gated — used while an upload is in flight (Figma 3790:25766).
 * The mock dims the banner photo to 55% brightness and the logo tile to 40%
 * (`filter:brightness`), rendered here as a black veil of the matching opacity.
 */
export const EVENT_HUB_IMAGE_OVERLAY_CLASS = 'pointer-events-none absolute inset-0 max-nav:hidden bg-black/45'
const LOGO_OVERLAY_CLASS = 'pointer-events-none absolute inset-0 max-nav:hidden bg-black/60'

const HOVER_OVERLAY_BASE = 'opacity-0 transition-opacity'
const HOVER_REVEAL_BASE = 'transition-opacity nav:opacity-0 nav:pointer-events-none'

export const EVENT_HUB_BANNER_HOVER_OVERLAY_CLASS = `${EVENT_HUB_IMAGE_OVERLAY_CLASS} ${HOVER_OVERLAY_BASE} nav:group-hover/banner:opacity-100 nav:group-focus-within/banner:opacity-100`
export const EVENT_HUB_BANNER_HOVER_REVEAL_CLASS = `${HOVER_REVEAL_BASE} nav:group-hover/banner:opacity-100 nav:group-hover/banner:pointer-events-auto nav:group-focus-within/banner:opacity-100 nav:group-focus-within/banner:pointer-events-auto`

export const EVENT_HUB_LOGO_HOVER_OVERLAY_CLASS = `${LOGO_OVERLAY_CLASS} ${HOVER_OVERLAY_BASE} nav:group-hover/logo:opacity-100 nav:group-focus-within/logo:opacity-100`
export const EVENT_HUB_LOGO_HOVER_REVEAL_CLASS = `${HOVER_REVEAL_BASE} nav:group-hover/logo:opacity-100 nav:group-hover/logo:pointer-events-auto nav:group-focus-within/logo:opacity-100 nav:group-focus-within/logo:pointer-events-auto`

/*
 * Banner image requirements behind the hero's "Size warning status" (Figma
 * 3790:25647) and quoted by its Edit tooltip. Kept EventHub-local on purpose:
 * Constants.UPLOAD.FILE_SIZE_LIMIT is 2MB and also governs registration and
 * gallery uploads, so the banner's 5MB allowance cannot ride on it.
 */
/**
 * Banner fit by ratio (mock `mountBannerFit`, 2 Oct 2026): an image at least this much wider than
 * its frame (ratio ÷ ratio) is a typographic band from the old site, shown WHOLE (object-fit:
 * contain) over a blurred, enlarged copy of itself; anything else (photos, taller images) keeps the
 * usual `cover` crop. Asymmetric on purpose: taller than the frame = a photo, cropped as today.
 */
export const EVENT_HUB_BANNER_FIT_WIDE = 1.15

export const isBannerWide = (
    naturalWidth,
    naturalHeight,
    frameWidth,
    frameHeight,
    threshold = EVENT_HUB_BANNER_FIT_WIDE,
) => {
    if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return false
    return naturalWidth / naturalHeight / (frameWidth / frameHeight) >= threshold
}

export const EVENT_HUB_BANNER_MIN_WIDTH = 1920
export const EVENT_HUB_BANNER_MIN_HEIGHT = 640
export const EVENT_HUB_BANNER_MAX_BYTES = 5 * 1024 * 1024

/**
 * Decodes a picked image file to measure it. Resolves `{ width, height }`, or null
 * when the browser cannot decode the file (wrong type, corrupt). Shared by the
 * upload dialog (every kind) and the banner check below.
 */
export const readImageDimensions = (file) =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        const settle = (result) => {
            URL.revokeObjectURL(url)
            resolve(result)
        }
        image.onload = () => settle({ width: image.naturalWidth, height: image.naturalHeight })
        image.onerror = () => settle(null)
        image.src = url
    })

/**
 * Resolves true when a picked banner file meets those requirements. Dimensions
 * are only knowable once the bitmap decodes, hence the promise; a file the
 * browser cannot decode counts as a rejection.
 */
export const isValidBannerImage = async (file) => {
    if (file.size > EVENT_HUB_BANNER_MAX_BYTES) {
        return false
    }
    const dimensions = await readImageDimensions(file)
    return (
        !!dimensions &&
        dimensions.width >= EVENT_HUB_BANNER_MIN_WIDTH &&
        dimensions.height >= EVENT_HUB_BANNER_MIN_HEIGHT
    )
}

/**
 * Light info tooltip (Figma 3790:24880) — #f7f7f9 body, #dbdadf hairline, 10px
 * text. Spread onto antd's Tooltip; `color` also tints the arrow.
 */
export const EVENT_HUB_TOOLTIP_PROPS = {
    color: '#f7f7f9',
    overlayInnerStyle: {
        border: '1px solid #dbdadf',
        borderRadius: '5px',
        color: '#272727',
        fontSize: '10px',
        lineHeight: '24px',
        minHeight: 'auto',
        padding: '2px 10px',
    },
}

/**
 * "Add new workout ⊞" / "New album ⊞" square (mock `.org-add-wod .sq`): 26×25, solid
 * ink, white 13px plus, 5px radius; the whole button hovers to #5b5f6e (--ink-2).
 */
export const EVENT_HUB_ADD_BUTTON_CLASS =
    's-focus-ring group flex shrink-0 cursor-pointer items-center gap-1.25 border-0 bg-transparent text-sm text-[#272727]'
export const EVENT_HUB_ADD_SQUARE_CLASS =
    'flex h-[25px] w-[26px] items-center justify-center rounded-[5px] bg-[#272727] text-white transition-colors group-hover:bg-[#5b5f6e]'

/**
 * Skin for the CKEditor instance while a description is being edited (mock `.ed.editing`
 * / Figma panel 3790:24468): one blue 10px-radius frame around a 54px white toolbar
 * (hairline below) and the content at 18px padding. CKEditor exposes its theme as
 * CSS custom properties, so the frame is restyled from the wrapper instead of a
 * stylesheet override.
 */
export const EVENT_HUB_RICH_EDITOR_CLASS = [
    'overflow-hidden rounded-[10px] border border-[#1672ec] bg-white',
    '[--ck-border-radius:0px]',
    '[--ck-color-base-border:transparent]',
    '[--ck-color-toolbar-background:#fff]',
    '[--ck-color-toolbar-border:#dbdadf]',
    '[--ck-color-focus-border:transparent]',
    '[--ck-focus-ring:1px_solid_transparent]',
    '[--ck-focus-outer-shadow:none]',
    '[--ck-inner-shadow:none]',
    '[&_.ck.ck-toolbar]:min-h-[54px]',
    '[&_.ck.ck-toolbar]:px-[15px]',
    // No BEM `__` hooks here: an underscore is a space inside an arbitrary variant (and the `\_`
    // escape is not honoured by this Tailwind), so `.ck-editor__top` would never match.
    // Mock `.ed-bar{border-bottom:1px solid var(--nav-bd)}`: the classic build zeroes the toolbar's
    // bottom border (`.ck-editor__top .ck-sticky-panel .ck-toolbar`, 0,4,0); restored at 0,5,0.
    '[&_.ck.ck-editor_.ck-sticky-panel_.ck-toolbar]:border-b',
    '[&_.ck.ck-editor_.ck-sticky-panel_.ck-toolbar]:border-b-[#dbdadf]',
    // `.ck-content` is the editable itself (beats `.ck.ck-editor__editable_inline{padding}`, 0,2,0).
    '[&_.ck.ck-editor_.ck-content]:p-[18px]',
].join(' ')

/**
 * Ghost button under an organizer empty state (mock `.ev-empty .btn.btn-ghost`): 33px, radius 5,
 * 16px sides, outline #dbdadf; 44px on phones (kit touch target under 760).
 */
export const EVENT_HUB_EMPTY_ACTION_CLASS =
    's-focus-ring inline-flex min-h-[33px] cursor-pointer items-center justify-center rounded-[5px] border border-[#dbdadf] bg-white px-4 text-sm text-[#272727] transition-colors hover:bg-[#f7f7f9] max-[760px]:min-h-11'

/**
 * Event title (mock `.banner-title`): SFT display 40/40 clamped to two lines; mobile 20/1.14. Shared
 * by the public h1 and the organizer's editable one, so the organizer sees what athletes see.
 */
export const EVENT_HUB_TITLE_CLASS =
    'font-display mb-0 line-clamp-2 text-[20px] font-bold leading-[1.14] nav:text-[40px] nav:leading-none'

/**
 * Banner title while the organizer side menu is pinned open (mock event.html
 * `body.org:not(.osd-min) .banner-title`): with the 24 px organizer gutters the band shrinks from 1328
 * to 1152 at 1440, so 40 × 1152/1328 ≈ 35 on three clamped lines. The shell sets `body.osd-on` /
 * `body.osd-min` (OrganizerShell/index.css); `osd-min` is also forced under 1100, so the variant only
 * ever applies on the wide layout.
 */
export const EVENT_HUB_PINNED_MENU_TITLE_CLASS =
    '[body.osd-on:not(.osd-min)_&]:line-clamp-3 [body.osd-on:not(.osd-min)_&]:text-[clamp(28px,2.5vw,35px)]'

/**
 * Venue map in the hub sidebar and the staff tunnel aside (Mapbox Static Images API, billed per
 * request, 50k free a month). Switched off on 2026-10-24: October ran to ~108k requests. The
 * long-term approach is still to be decided; set to true to bring the map back.
 */
export const STATIC_MAP_ENABLED = false

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

/**
 * Static map URL centred on the venue. With a logo the caller draws it as a centred HTML overlay
 * (Mapbox custom markers render at native size and reject images >1024px), so the map itself stays
 * marker-free; without one, fall back to Mapbox's default dark pin. Null when the map is switched
 * off, the token is missing or the coordinates are unusable.
 */
export const buildStaticMapUrl = (longitude, latitude, logo) => {
    if (!STATIC_MAP_ENABLED || !MAPBOX_TOKEN || longitude == null || latitude == null) return null
    const lng = parseFloat(longitude)
    const lat = parseFloat(latitude)
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
    const marker = logo ? '' : `pin-s+272727(${lng},${lat})/`
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${marker}${lng},${lat},16,0/602x338@2x?access_token=${MAPBOX_TOKEN}`
}
